// zod-schema-builder.js
import { z } from "zod";
import { validatorRegistry } from "./validator-registry";
import { isEmpty } from "./utils/validator-utils";

export class ZodSchemaBuilder {
    buildFieldSchema(field) {
        const type = field.getAttribute("data-zod-type");
        const rules = field.getAttribute("data-zod-rules")?.split(",") || [];
        const isRequired = field.hasAttribute("data-zod-required");

        // Get the field's label text
        const labelText = this._getFieldLabelText(field);

        // Start with base schema based on type
        let schema;
        switch (type) {
            case "email":
                schema = z.string();
                break;
            // TODO: Number field UX issue - Users can type 'e' and '.' characters during input
            // but validation only occurs on blur/submit events, not real-time.
            //
            // Current behavior:
            // - 'e' and '.' are valid parts of numbers (scientific notation: 1e2, decimals: 3.14)
            // - Invalid standalone 'e' or '.' will be caught during validation
            // - But users get no immediate feedback while typing invalid combinations
            //
            // Potential solutions to consider:
            // 1. Add real-time input validation with debouncing
            // 2. Use input[type="number"] with pattern attribute for stricter input filtering
            // 3. Add custom input event handler to prevent invalid character sequences
            // 4. Implement progressive validation (validate as user types valid sequences)
            // 5. Create integerOnly & floatOnly validators for more granular number control:
            //    - integerOnly: Reject decimals and scientific notation (only whole numbers)
            //    - floatOnly: Allow decimals but reject scientific notation (no 'e' characters)
            //    - This would give content creators choice over number format strictness
            //    - Usage: data-zod-rules="integerOnly" or data-zod-rules="floatOnly"
            //
            // Note: Current validation should correctly handle all edge cases but UX could be improved
            case "number":
                // Check if floatOnly rule exists
                if (rules.includes("floatOnly")) {
                    schema = z.string().refine(
                        val => {
                            if (isEmpty(val)) {
                                return true;
                            }
                            const parsed = Number(val);
                            if (isNaN(parsed)) {
                                return false;
                            }
                            return true;
                        },
                        {
                            message: field.getAttribute("data-error-number") || "Please enter a valid number"
                        }
                    );
                } else {
                    schema = z.string().transform((val, ctx) => {
                        const parsed = Number(val);
                        if (isNaN(parsed)) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: field.getAttribute("data-error-number") || "Please enter a valid number"
                            });
                            return z.NEVER;
                        }
                        return parsed;
                    });
                }
                break;
            case "tel":
                schema = z.string();
                break;
            default:
                schema = z.string();
        }

        // Apply validation rules
        const options = { labelText };

        if (isRequired) {
            const requiredValidator = validatorRegistry.get("required");
            schema = requiredValidator.getZodSchema(schema, options);
        }

        // Apply additional rules
        for (const rule of rules) {
            const [name, value, message] = rule.split("=");
            if (validatorRegistry.has(name)) {
                const validator = validatorRegistry.get(name);
                const ruleOptions = {
                    ...options,
                    value,
                    message
                };
                schema = validator.getZodSchema(schema, ruleOptions);
            }
        }

        // Make optional if not required
        return isRequired ? schema : schema.optional();
    }

    _getFieldLabelText(field) {
        let labelText = "This field";
        const fieldId = field.getAttribute("id");
        if (fieldId) {
            const labelElement = document.querySelector(`label[for="${fieldId}"]`);
            if (labelElement) {
                labelText = labelElement.textContent
                    .replace(/\s*\*\s*$/, "")
                    .replace(/\s+/g, " ")
                    .trim();
            }
        }

        if (labelText === "This field") {
            labelText = field.getAttribute("aria-label") || field.getAttribute("name") || "This field";
        }

        return labelText;
    }
}

export const schemaBuilder = new ZodSchemaBuilder();
